import React, { useState } from 'react';
import { Card, Typography, Button, Table, Space, Tag, Modal, Form, Input, Popconfirm, Grid } from 'antd';
import { TagsOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useCategoriasClientes } from '../../../hooks/useCategoriasClientes';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const SeccionCategoriasClientes = () => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const { categorias, isLoading, saveCategoria, deleteCategoria } = useCategoriasClientes();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
    const [form] = Form.useForm();

    const handleOpenModal = (record = null) => {
        setIsEditing(!!record);
        setCategoriaSeleccionada(record);
        if (record) {
            form.setFieldsValue({
                nombre: record.nombre,
                descripcion: record.descripcion
            });
        } else {
            form.resetFields();
        }
        setIsModalOpen(true);
    };

    const handleSubmit = (values) => {
        const payload = {
            ...values,
            id: isEditing ? categoriaSeleccionada.id : null
        };

        saveCategoria.mutate(payload, {
            onSuccess: () => {
                setIsModalOpen(false);
                form.resetFields();
            }
        });
    };

    const columns = [
        {
            title: 'Nombre de la Categoría',
            dataIndex: 'nombre',
            render: (text) => (
                <Space>
                    <TagsOutlined style={{ color: '#22BABB' }} />
                    <Text strong style={{ fontSize: '14px' }}>{text}</Text>
                </Space>
            )
        },
        {
            title: 'Descripción',
            dataIndex: 'descripcion',
            render: (text) => text || <Text type="secondary" italic>Sin descripción</Text>
        },
        {
            title: 'Clientes Asignados',
            dataIndex: 'total_clientes',
            align: 'center',
            render: (total) => <Tag color="purple">{total || 0} clientes</Tag>
        },
        {
            title: 'Acciones',
            align: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleOpenModal(record)}
                        style={{ color: '#22BABB' }}
                    />
                    <Popconfirm
                        title="¿Eliminar esta categoría?"
                        description="Los clientes vinculados no se borrarán; pasarán a 'Sin Categoría'."
                        onConfirm={() => deleteCategoria.mutate(record.id)}
                        okText="Sí, eliminar"
                        cancelText="Cancelar"
                        okButtonProps={{ danger: true }}
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{ animation: 'fadeIn 0.3s' }}>
            {/* ENCABEZADO */}
            <div style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row', 
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'stretch' : 'center', 
                gap: '12px',
                marginBottom: '16px' 
            }}>
                {!isMobile && (
                    <div>
                        <Title level={4} style={{ margin: 0 }}>Tipos de Cliente (Categorías)</Title>
                        <Text type="secondary" style={{ fontSize: '13px' }}>Crea y gestiona las categorías para segmentar a tus clientes.<br/></Text>
                        <Text type="secondary" style={{ fontSize: '13px' }}>Una vez ingrese opciones al sistema este te mostrará campos en el modulo de clientes al momento de crearlos</Text>
                    </div>
                )}
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size={isMobile ? "middle" : "large"}
                    onClick={() => handleOpenModal()}
                    style={{ backgroundColor: '#22BABB', borderRadius: '8px', fontWeight: 'bold' }}
                    block={isMobile}
                >
                    Nueva Categoría
                </Button>
            </div>

            {/* CONTENIDO: TABLA EN ESCRITORIO / CARDS EN MÓVIL */}
            {!isMobile ? (
                <Card bordered={false} style={{ borderRadius: '16px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    <Table
                        columns={columns}
                        dataSource={categorias}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{ pageSize: 10 }}
                        locale={{ emptyText: 'No has creado ninguna categoría aún.' }}
                    />
                </Card>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {categorias.length === 0 && !isLoading && (
                        <Card size="small" style={{ textAlign: 'center', padding: '20px 0' }}>
                            <Text type="secondary">No has creado ninguna categoría aún.</Text>
                        </Card>
                    )}
                    {categorias.map((item) => (
                        <Card 
                            key={item.id} 
                            size="small" 
                            style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                <Space>
                                    <TagsOutlined style={{ color: '#22BABB', fontSize: '16px' }} />
                                    <Text strong style={{ fontSize: '15px' }}>{item.nombre}</Text>
                                </Space>
                                <Tag color="purple" style={{ margin: 0 }}>{item.total_clientes || 0} clientes</Tag>
                            </div>

                            {item.descripcion && (
                                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '10px' }}>
                                    {item.descripcion}
                                </Text>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', borderTop: '1px solid #f1f5f9', paddingTop: '8px', marginTop: '6px' }}>
                                <Button
                                    size="small"
                                    type="outlined"
                                    icon={<EditOutlined />}
                                    onClick={() => handleOpenModal(item)}
                                    style={{ color: '#22BABB', borderColor: '#22BABB', fontSize: '12px' }}
                                >
                                    Editar
                                </Button>
                                <Popconfirm
                                    title="¿Eliminar esta categoría?"
                                    onConfirm={() => deleteCategoria.mutate(item.id)}
                                    okText="Sí"
                                    cancelText="No"
                                    okButtonProps={{ danger: true }}
                                >
                                    <Button size="small" danger icon={<DeleteOutlined />} style={{ fontSize: '12px' }}>
                                        Eliminar
                                    </Button>
                                </Popconfirm>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* MODAL ADAPTADO PARA MÓVIL Y DESKTOP */}
            <Modal
                title={
                    <Space size="small" align="center">
                        <div style={{ background: '#ccfbf1', padding: '8px', borderRadius: '10px', display: 'flex' }}>
                            <TagsOutlined style={{ color: '#0f766e', fontSize: '18px' }} />
                        </div>
                        <span style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
                            {isEditing ? 'Editar Categoría' : 'Nueva Categoría'}
                        </span>
                    </Space>
                }
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                centered
                width={isMobile ? '95%' : 500}
                styles={{ body: { paddingTop: '12px' } }}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="nombre"
                        label={<Text strong style={{ fontSize: '13px', color: '#334155' }}>Nombre de la Categoría</Text>}
                        rules={[{ required: true, message: 'El nombre es obligatorio' }]}
                    >
                        <Input
                            style={{ height: '44px', borderRadius: '10px', fontSize: '14px' }}
                            placeholder="Ej: Mayorista, Tienda, VIP..."
                        />
                    </Form.Item>

                    <Form.Item
                        name="descripcion"
                        label={<Text strong style={{ fontSize: '13px', color: '#334155' }}>Descripción <Text type="secondary" style={{ fontWeight: 'normal' }}>(Opcional)</Text></Text>}
                    >
                        <Input.TextArea
                            rows={3}
                            style={{ borderRadius: '10px', fontSize: '13px' }}
                            placeholder="Ej: Clientes con volumen de compra alto..."
                        />
                    </Form.Item>

                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        loading={saveCategoria.isPending}
                        style={{ backgroundColor: '#22BABB', height: '46px', borderRadius: '10px', fontSize: '15px', fontWeight: '700', marginTop: '10px', border: 'none' }}
                    >
                        {isEditing ? 'ACTUALIZAR CATEGORÍA' : 'GUARDAR CATEGORÍA'}
                    </Button>
                </Form>
            </Modal>
        </div>
    );
};

export default SeccionCategoriasClientes;